import { faArrowRight, faArrowUp } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconButton, makeStyles, Typography } from "@material-ui/core"

import { SafetyPlanResponseOptionType } from "../../../utils/constants"
import {
  keyToIconDef,
  Option,
  SafetyPlanSectionPractitionerProps,
} from "../../../utils/safetyPlanUtils"
import { colors } from "../../../utils/theme"
import Categories from "./Categories"

/**
 * A collapsible section card used in the practitioner safety plan editor.
 *
 * Displays the section header with a collapse/expand toggle, the section
 * description, and an inline editable list of category option groups via
 * `Categories`. The TEXT_BOX category is hoisted to the top of the list.
 *
 * @param selected     - The full section definition including its optionsMap.
 * @param selection    - The current list of selected items across all sections.
 * @param onClick      - Callback invoked when the user selects or toggles an option.
 * @param onChange     - Callback invoked when free-text content changes.
 * @param openSection  - Callback invoked with the section id to toggle collapse state.
 * @param collapsed    - Array of section ids that are currently collapsed.
 */
export default function EditableSafetyPlanSection({
  selected,
  selection,
  onClick,
  onChange,
  openSection,
  collapsed,
}: SafetyPlanSectionPractitionerProps) {
  const classes = useStyles()

  const optionsArr = [...selected.optionsMap.values()]

  const foundIdx = optionsArr.findIndex(
    (el) => el.category_type === SafetyPlanResponseOptionType.TEXT_BOX
  )

  if (foundIdx !== -1) {
    const customEle = optionsArr[foundIdx]
    optionsArr.splice(foundIdx, 1)
    optionsArr.unshift(customEle)
  }

  let descriptionText = selected.description
  if (selected.header.includes("Myself Safe")) {
    descriptionText = descriptionText.replace(/<br\s*\/?>/gi, "")
  }

  const selectedOptions = optionsArr.map((d: Option, index: number) => (
    <Categories
      category={d}
      selection={selection}
      onChange={onChange}
      onClick={onClick}
      newSafetyPlanView={true}
      key={index.toString()}
    />
  ))

  const isCollapsed = collapsed.some((el: any) => el === selected.id)

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeader}>
        <div
          className={classes.iconContainer}
          style={{ background: selected.icon_color }}
        >
          <FontAwesomeIcon icon={keyToIconDef(selected.icon)} />
        </div>
        <Typography
          className={classes.sectionHeaderText}
          component="div"
          dangerouslySetInnerHTML={{ __html: selected.header }}
        />
        <IconButton
          className={classes.sectionCheck}
          onClick={() => openSection(selected.id)}
        >
          <FontAwesomeIcon icon={isCollapsed ? faArrowRight : faArrowUp} />
        </IconButton>
      </div>

      <div
        className={
          isCollapsed ? classes.sectionContentCollapsed : classes.sectionContent
        }
        id={selected.id}
      >
        <Typography
          className={classes.sectionContentText}
          component="div"
          dangerouslySetInnerHTML={{ __html: descriptionText }}
        />
        {selectedOptions}
      </div>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  sectionContainer: {
    marginBottom: "20px",
  },
  sectionHeader: {
    display: "flex",
    padding: "12px 20px",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
    borderRadius: "8px 8px 0px 0px",
    borderBottom: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.neutral.white,
  },
  sectionHeaderText: {
    color: colors.safetyPlan.ink,
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: "20px",
  },
  sectionCheck: {
    display: "flex",
    padding: "12px",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    borderRadius: "360px",
    background: "rgba(0, 0, 0, 0.03)",
    marginLeft: "auto",
    fontSize: "1rem",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.03)",
    },
  },
  sectionContent: {
    display: "flex",
    padding: "16px 20px 24px 20px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
    alignSelf: "stretch",
    background: colors.safetyPlan.cardSecondaryBackground,
  },
  sectionContentCollapsed: {
    display: "none",
  },
  sectionContentText: {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "20px",
    color: colors.neutral.asphalt,
  },
  iconContainer: {
    display: "flex",
    width: "36px",
    height: "36px",
    padding: "6.923px 8.308px 9.692px 8.308px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "360px",
    color: colors.neutral.white,
  },
}))
